// src/feed/useFeed.ts
//
// Realtime feed hook. Three modes selectable by caller:
//
//   • 'following' — items from people the viewer follows. Reads
//     /users/{me}/home_feed/{item_id}, the materialized fan-out
//     written by the feedFanOut Cloud Function. The fan-out also
//     writes the user's own items to their own home_feed (so a
//     user sees their own posts in "everything in my world"
//     mode), but for the Following tab we filter those out so it
//     means strictly "people I follow." Yours tab covers own
//     items on its own.
//
//   • 'timeline' — the viewer's own items, queried directly from
//     /feed_items where owner_id == self. Uses the existing
//     owner_id + created_at composite index. This is the user's
//     personal timeline — the spine of the spatial-memory model.
//
//   • 'public' — every public item, queried from /feed_items
//     where visibility == 'public'. Requires a new composite
//     index on (visibility ASC, created_at DESC). The full
//     firehose across the platform — discovery surface.
//
// Visibility filtering:
//   • 'following' is server-enforced via the fan-out (only items
//     the viewer is allowed to see ever land in their home_feed).
//   • 'timeline' is owner-scoped — the rule allows owners to read
//     their own items regardless of visibility.
//   • 'public' is naturally restricted by the where clause itself.
//
// Pagination: not yet. FEED_LIMIT caps each tab. Pagination earns
// its slot when users hit the cap regularly.

import { useEffect, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { FeedItem } from './types';

export type FeedMode = 'following' | 'timeline' | 'public';

const FEED_LIMIT = 100;

export interface UseFeedResult {
  items: FeedItem[];
  loading: boolean;
  error: string | null;
}

/** Hydrate a Firestore doc into a FeedItem with safe defaults.
 *  Used for both /home_feed and /feed_items snapshots since they
 *  share schema. */
function toFeedItem(d: { id: string; data: () => any }): FeedItem {
  const data = d.data() as Partial<FeedItem>;
  return {
    id: d.id,
    owner_id: data.owner_id ?? '',
    author: data.author ?? { display_name: 'Anonymous traveler' },
    kind: data.kind ?? ('photo' as any),
    visibility: data.visibility ?? 'private',
    created_at: (data.created_at ?? null) as Timestamp | null,
    updated_at: (data.updated_at ?? null) as Timestamp | null,
    snapshot: data.snapshot ?? {},
    source: data.source ?? { kind: 'manual', id: '' },
    reactions_count: data.reactions_count ?? 0,
    comments_count: data.comments_count ?? 0,
    reaction_target: data.reaction_target,
    share_enabled: data.share_enabled,
    source_visibility: data.source_visibility,
  };
}

export function useFeed(mode: FeedMode = 'following'): UseFeedResult {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const user = auth.currentUser;

    // 'following' and 'timeline' identify content by viewer uid;
    // they require auth. 'public' could in principle work without
    // auth, but the firestore.rules currently require it for all
    // feed_items reads (apart from share_enabled docs). Either
    // way this guard is the right local behavior.
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let q;
    if (mode === 'following') {
      q = query(
        collection(db, 'users', user.uid, 'home_feed'),
        orderBy('created_at', 'desc'),
        limit(FEED_LIMIT)
      );
    } else if (mode === 'timeline') {
      q = query(
        collection(db, 'feed_items'),
        where('owner_id', '==', user.uid),
        orderBy('created_at', 'desc'),
        limit(FEED_LIMIT)
      );
    } else {
      // public
      q = query(
        collection(db, 'feed_items'),
        where('visibility', '==', 'public'),
        orderBy('created_at', 'desc'),
        limit(FEED_LIMIT)
      );
    }

    const unsub = onSnapshot(
      q,
      (snap) => {
        let next: FeedItem[] = snap.docs.map(toFeedItem);
        // Following = people you follow. The fan-out also writes
        // the user's own items into their own home_feed for the
        // "everything in my world" semantic — strip those here so
        // the Following tab is strictly OTHER PEOPLE'S posts. Own
        // posts surface in the Timeline tab.
        if (mode === 'following') {
          next = next.filter((it) => it.owner_id !== user.uid);
        }
        setItems(next);
        setLoading(false);
      },
      (err) => {
        console.warn('useFeed: snapshot listener error:', err, { mode });
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [mode]);

  return { items, loading, error };
}
